import { Component, Input, Output, EventEmitter } from '@angular/core';
import { OrderProcessingLine, TreeNode, TreeNodeAggregates, TreeNodeCriteria } from '@equipmyschool/emsadminweb-models';

@Component({
  selector: 'app-orderline-grid',
  templateUrl: './orderline-grid.component.html',
  styleUrls: ['./orderline-grid.component.scss']
})
export class OrderlineGridComponent {
  @Input() datasource: OrderProcessingLine[] | null = null;
  @Input() selectedNode: TreeNode | null = null;
  @Output() selectionChanged = new EventEmitter();

  // Returns the datasource filtered by `selectedNode` criteria, grouped by `aggregates`,
  // with sums for columns in `sum`. If `count` is true a `LineCount` property is added.
  get processedData(): any[] {
    if (!this.datasource) return [];

    // filtering
    let filtered = this.datasource;
    // support selectedNode.criteria as object or selectedNode.filter function
    if (this.selectedNode && this.selectedNode.criteria && this.selectedNode.criteria.length > 0) {
      const criteria = this.selectedNode.criteria;
      filtered = filtered.filter((r: any) => criteria.every((k: TreeNodeCriteria) => {
        return String((r as any)[k.name]) === k.id;
      }));
    }

    // determine aggregates config from selectedNode.aggregates: { grouped: TreeNodeColumn[], sum: TreeNodeColumn[], count: boolean }
    const nodeAgg: TreeNodeAggregates | null = this.selectedNode?.aggregates ?? null;
    let groupedCols: {id:string,label:string}[] | null = null;
    let sumCols: {id:string,label:string}[] | null = null;
    let countFlag = this.selectedNode?.aggregates?.count ?? false;
    if (nodeAgg) {
      groupedCols = Array.isArray(nodeAgg.group) ? nodeAgg.group : null;
      sumCols = Array.isArray(nodeAgg.sum) ? nodeAgg.sum : null;
      countFlag = !!nodeAgg.count;
    }

    // fallback: if no groupedCols configured, return filtered (with per-row LineCount if requested)
    if (!groupedCols || groupedCols.length === 0) {
      if (countFlag) return filtered.map(r => ({...r, LineCount: 1}));
      return filtered;
    }

    // perform grouping: use `label` to read value from row, use `id` as output property name
    const groups = new Map<string, any>();
    for (const row of filtered) {
      const key = groupedCols.map(col => String((row as any)[col.id] ?? '')).join('||');
      if (!groups.has(key)) {
        const g: any = {};
        for (const col of groupedCols) {
          g[col.label] = (row as any)[col.id];
        }
        if (sumCols) for (const s of sumCols) g[s.label] = Number((row as any)[s.id] || 0);
        g.LineCount = 1;
        groups.set(key, g);
      } else {
        const g = groups.get(key);
        if (sumCols) for (const s of sumCols) g[s.label] = (Number(g[s.label]) || 0) + (Number((row as any)[s.id]) || 0);
        g.LineCount = (g.LineCount || 0) + 1;
      }
    }

    const result = Array.from(groups.values()).map(g => {
      const out = {...g};
      if (!countFlag) delete out.LineCount;
      return out;
    });

    result.sort((a,b) => {
      for (const col of this.selectedNode?.sort || []) {
        const aVal = String(a[col] ?? '');
        const bVal = String(b[col] ?? '');
        const cmp = aVal.localeCompare(bVal);
        if (cmp !== 0) return cmp;
      }
      return 0;
    });

    return result;
  }

  onRowClick(row: any) {
    const id = row[this.selectedNode?.selectionColumn as keyof OrderProcessingLine];
    const filtered = this.datasource?.filter(r => {
      return this.selectedNode?.criteria?.every(c => {
        const val = r[c.name as keyof OrderProcessingLine];
        return String(val) === String(c.id);
      }) && (r[this.selectedNode?.selectionColumn as keyof OrderProcessingLine] === id);
    });

    const allChecked = filtered?.every(s=>s.Selected) ?? false;
    
    this.datasource?.forEach(r => {
      if (filtered?.includes(r)) {
        r.Selected = !allChecked;
      }
    });

    this.selectionChanged.emit();
  }

  checkSelected(row: any): boolean {
    return this.datasource?.filter(r => {
      return this.selectedNode?.criteria?.every(c => {
        const val = r[c.name as keyof OrderProcessingLine];
        return String(val) === String(c.id);
      }) && (r[this.selectedNode?.selectionColumn as keyof OrderProcessingLine] === row[this.selectedNode?.selectionColumn as keyof OrderProcessingLine]);
    }).every(r => r.Selected) ?? false;
  }
}
